/**
 * AttestationDocuments — case a cocher d'attestation "documents administratifs a jour".
 *
 * POURQUOI ce composant existe :
 * Avant d'envoyer / accepter une offre (onglet Offre du BC comme du MS), le prestataire doit
 * attester que ses documents administratifs (assurances, declarations fiscales/sociales...) sont
 * a jour dans son profil. C'est une exigence reglementaire des marches publics : l'acheteur engage
 * la commande sur la foi de documents valides. L'attestation est donc OBLIGATOIRE (garde front +
 * back 422) et HORODATEE au moment de l'engagement (le parent persiste attestation_documents_at).
 *
 * Composant PARTAGE (consomme par SuiviDeCommandeBCV2 ET SuiviDeCommandeMSV2). Il gere lui-meme
 * l'ouverture de la modale de profil sur l'onglet Documents ; le parent ne pilote que l'etat de la
 * case (checked / onChange) et l'erreur d'obligation (error).
 *
 * Dispositif VOLONTAIREMENT DISTINCT des CGV (CGVAccept) : preuve datee differente, texte different,
 * pas de scroll obligatoire. Ne pas fusionner les deux.
 */
import React, { useCallback, useId, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import ModalProfilMembre from '../../pages/Publications/components/DetailCandidature/ModalProfilMembre';
import { getProfile } from '../../services/profileService';
import type { ProfileData } from '../../services/profileService';
import './AttestationDocuments.css';

// L'onglet "Documents" de ModalProfilMembre est a l'index 1
// (MEMBER_TABS = ['Présentation', 'Documents', 'Références', 'Bancaire', "Gestion d'équipe"]).
const DOCUMENTS_TAB_INDEX = 1;

interface AttestationDocumentsProps {
  /** Etat de la case, pilote par le parent. Son etat initial reflete attestation_documents_at. */
  checked: boolean;
  onChange: (checked: boolean) => void;
  /**
   * Id de l'entite/prestataire concernee. ACCEPTE pour symetrie d'API entre les 2 modules, mais le
   * chargement du profil ne s'en sert PAS : sur l'onglet Offre le prestataire consulte SON PROPRE
   * profil, on lit donc /profile (voir openProfil). L'id reste dispo pour un besoin futur (ex.
   * consultation cote acheteur) sans casser la signature.
   */
  entiteId?: number;
  /** Message d'obligation pose par le parent (regle 4) : rendu sous la case via 'error-message'. */
  error?: string | null;
  /** Desactive l'interaction (ex. envoi en cours). La case N'EST PAS figee apres l'envoi. */
  disabled?: boolean;
}

const AttestationDocuments: React.FC<AttestationDocumentsProps> = ({
  checked,
  onChange,
  error,
  disabled = false,
}) => {
  const navigate = useNavigate();
  // Id unique et stable : permet de lier <label htmlFor> a l'<input> meme si plusieurs instances
  // coexistent (utile car ce composant est partage par 2 modules).
  const checkboxId = useId();

  // Etat local de la modale de profil : le composant s'auto-suffit, le parent n'a rien a gerer.
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);

  // Ouvre la modale sur l'onglet Documents et charge le profil du prestataire connecte.
  // ROUTE RETENUE : /profile (profileService.getProfile), et NON /annuaire/entites/{id}/profil.
  // POURQUOI : la route annuaire exige entite.visible=true et renverrait 403 pour un prestataire
  // non publie dans l'annuaire ; or ici le prestataire consulte SON PROPRE profil sur SA commande.
  // /profile est authentifie par son token et retourne sa propre entite sans condition de visibilite,
  // avec documents / documents_status / contrats_ac / documents_communs — tout ce dont l'onglet
  // Documents (ReadOnlyDocuments) a besoin.
  const openProfil = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      // Le bouton est a l'interieur du <label> : sans cela, le clic sur le lien basculerait aussi
      // la case a cocher. On stoppe la propagation vers le label pour n'ouvrir QUE la modale.
      e.preventDefault();
      e.stopPropagation();

      setIsModalOpen(true);
      setProfile(null);
      setProfileError(null);
      setProfileLoading(true);
      getProfile()
        .then(setProfile)
        .catch((err: unknown) =>
          setProfileError(err instanceof Error ? err.message : 'Impossible de charger le profil.'),
        )
        .finally(() => setProfileLoading(false));
    },
    [],
  );

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setProfile(null);
    setProfileError(null);
  }, []);

  return (
    <div className="attestation-documents">
      <div className="attestation-documents-row">
        <input
          type="checkbox"
          id={checkboxId}
          // La case passe en classe 'error' quand le parent signale l'obligation non remplie
          // (piege repo : une erreur non rendue = bouton mysterieusement inerte cote parent).
          className={`attestation-documents-checkbox${error ? ' error' : ''}`}
          checked={checked}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
        />
        <label htmlFor={checkboxId} className="attestation-documents-label">
          {"J'atteste que "}
          {/* Lien orange souligne : un <button> inline (pas de <a>) car il n'y a AUCUNE navigation,
              seulement l'ouverture de la modale de profil sur l'onglet Documents. */}
          <button
            type="button"
            className="attestation-documents-link"
            onClick={openProfil}
            disabled={disabled}
          >
            mes documents administratifs
          </button>
          {' dans mon profil (ex : assurances, déclarations...) sont à jour.'}
        </label>
      </div>

      {/* Erreur d'obligation, rendue avec la classe globale 'error-message' pour rester coherent
          avec les autres champs du repo. */}
      {error && <span className="error-message attestation-documents-error">{error}</span>}

      {/* Modale de profil montee via portal sur document.body : neutralise les cascades z-index et
          les selecteurs descendants du conteneur parent (meme precaution que ProfilCardPrestataire),
          pour un rendu identique quel que soit le module hote (BC ou MS). */}
      {createPortal(
        <div style={{ position: 'relative', zIndex: 100000 }}>
          <ModalProfilMembre
            isOpen={isModalOpen}
            onClose={closeModal}
            profile={profile}
            isLoading={profileLoading}
            error={profileError}
            showCompletion={false}
            // Ouvre directement l'onglet Documents (l'attestation porte sur ces documents).
            initialTab={DOCUMENTS_TAB_INDEX}
            // Le prestataire regarde SON propre profil : on expose le bouton "Modifier" qui le renvoie
            // dans /profil au bon onglet. Les onglets de la modale et de /profil portent les memes
            // noms (ProfilUnifie resout ?tab= par nom), d'ou l'envoi du nom d'onglet, sans index magique.
            showModifier
            onModifier={(tabName) => navigate(`/profil?tab=${encodeURIComponent(tabName)}`)}
          />
        </div>,
        document.body,
      )}
    </div>
  );
};

export default AttestationDocuments;
